<template>
  <div class="trigger-capture-page">
    <div class="page-header">
      <h1>Trigger Configuration Capture</h1>
      <p class="subtitle">
        Create a snapshot of a client's M365 configuration for review
      </p>
    </div>

    <div class="content-card">
      <!-- Client Selection -->
      <div class="form-section">
        <label for="clientSelect" class="form-label">
          Select Client <span class="required">*</span>
        </label>
        <select
          id="clientSelect"
          v-model="selectedClientCode"
          class="form-select"
          :disabled="isRunning"
        >
          <option value="">-- Select a client --</option>
          <option
            v-for="client in clients"
            :key="client.code"
            :value="client.code"
          >
            {{ client.code }} - {{ client.name }}
          </option>
        </select>
        <p class="help-text">
          Choose the client whose configuration you want to capture
        </p>
      </div>

      <!-- Advanced Options (Collapsible) -->
      <div class="form-section">
        <button
          class="toggle-advanced"
          @click="showAdvanced = !showAdvanced"
          type="button"
        >
          {{ showAdvanced ? "▼" : "▶" }} Advanced Options
        </button>

        <div v-if="showAdvanced" class="advanced-options">
          <div class="form-group">
            <label class="form-label">Excluded Properties (Optional)</label>
            <textarea
              v-model="excludedPropertiesText"
              class="form-textarea"
              rows="4"
              placeholder="e.g., id, createdDateTime, lastModifiedDateTime&#10;One per line or comma-separated"
              :disabled="isRunning"
            ></textarea>
            <p class="help-text">
              Properties to exclude from comparison (e.g., timestamps, GUIDs)
            </p>
          </div>

          <div class="form-group">
            <label class="form-label">Excluded Paths (Optional)</label>
            <textarea
              v-model="excludedPathsText"
              class="form-textarea"
              rows="4"
              placeholder="e.g., /M365Config/Resources/Descriptions&#10;/M365Config/Resources/Office365&#10;One per line"
              :disabled="isRunning"
            ></textarea>
            <p class="help-text">Configuration paths to exclude from capture</p>
          </div>

          <div class="form-group">
            <label class="form-label">Baseline Version</label>
            <input
              v-model="baselineVersion"
              type="text"
              class="form-input"
              placeholder="e.g., 1.0.0"
              :disabled="isRunning"
            />
            <p class="help-text">
              Current baseline version for metadata tracking
            </p>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-section">
        <button
          class="btn-primary"
          @click="triggerCapture"
          :disabled="!selectedClientCode || isRunning"
        >
          <span v-if="isRunning" class="spinner-small"></span>
          {{ isRunning ? "Capturing..." : "🚀 Trigger Capture" }}
        </button>

        <button
          v-if="captureCompleted"
          class="btn-secondary"
          @click="goToReview"
        >
          📋 Go to Review
        </button>
      </div>

      <!-- Status Display -->
      <div v-if="pipelineStatus.message" class="status-section">
        <div :class="['status-card', pipelineStatus.type]">
          <div class="status-header">
            <span class="status-icon">
              {{ getStatusIcon(pipelineStatus.type) }}
            </span>
            <span class="status-title">{{
              getStatusTitle(pipelineStatus.type)
            }}</span>
          </div>
          <p class="status-message">{{ pipelineStatus.message }}</p>

          <!-- Progress Bar -->
          <div
            v-if="isRunning && pipelineStatus.progress"
            class="progress-section"
          >
            <div class="progress-bar">
              <div
                class="progress-fill"
                :style="{ width: pipelineStatus.progress + '%' }"
              ></div>
            </div>
            <p class="progress-text">{{ pipelineStatus.progress }}% Complete</p>
          </div>

          <!-- Branch Info (when complete) -->
          <div v-if="captureCompleted && branchName" class="branch-info">
            <h4>📌 Capture Branch Created</h4>
            <code>{{ branchName }}</code>
            <p class="help-text">
              This branch contains the captured configuration and is ready for
              review
            </p>
          </div>
        </div>
      </div>

      <!-- Info Box -->
      <div class="info-box">
        <h3>ℹ️ How it Works</h3>
        <ol>
          <li>
            <strong>Capture:</strong> The pipeline connects to the client's M365
            tenant and exports the current configuration
          </li>
          <li>
            <strong>Branch Creation:</strong> Creates a new branch with format
            <code>client-{CODE}-capture-{timestamp}</code>
          </li>
          <li>
            <strong>Review:</strong> Navigate to Configuration Capture
            Management to review and approve changes
          </li>
          <li>
            <strong>Merge:</strong> Once approved, merge the changes back to the
            main branch
          </li>
        </ol>
      </div>
    </div>
  </div>
</template>

<script>
import {
  fetchClients,
  runCapturePipeline,
  getPipelineStatus,
} from "@/azure-devops-api.js";
import { getAccessToken } from "@/services/auth-utils.js";

export default {
  name: "TriggerCapture",

  data() {
    return {
      clients: [],
      selectedClientCode: "",

      // Advanced options
      showAdvanced: false,
      excludedPropertiesText: "",
      excludedPathsText: "",
      baselineVersion: "1.0.0",

      // Pipeline state
      isRunning: false,
      captureCompleted: false,
      branchName: "",
      pipelineStatus: {
        message: "",
        type: "", // info, success, error, warning
        progress: 0,
      },

      // Pipeline details
      projectId: "1b238677-4a8d-4243-827e-88a0558c085a",
      pipelineId: 216,
      runId: null,
      pollInterval: null,
    };
  },

  async created() {
    await this.loadClients();
    await this.loadPipelineId();
  },

  beforeUnmount() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
  },

  methods: {
    async loadClients() {
      try {
        const accessToken = await this.getAccessToken();
        const repoId = "62b297c6-760c-4250-a926-52b8850e6d5f";

        const clientCodes = await fetchClients(accessToken, repoId, "/Clients");

        this.clients = clientCodes.map((code) => ({
          code: code,
          name: `Client ${code}`,
        }));

        this.clients.sort((a, b) => a.code.localeCompare(b.code));
      } catch (error) {
        console.error("Error loading clients:", error);
        this.pipelineStatus = {
          message: "Failed to load clients: " + error.message,
          type: "error",
        };
      }
    },

    async loadPipelineId() {
      // TODO: Fetch the pipeline ID for "Dynamic Client Configuration Capture"
      // For now, you'll need to set this manually after creating the pipeline
      // this.pipelineId = "123"; // Replace with actual pipeline ID
      // You can add code here to search for the pipeline by name
      // using fetchPipelinesWithFilterandProject
    },

    async triggerCapture() {
      if (!this.selectedClientCode) return;

      if (!this.pipelineId) {
        this.pipelineStatus = {
          message:
            "Pipeline not configured. Please set the pipeline ID in the code.",
          type: "error",
        };
        return;
      }

      this.isRunning = true;
      this.captureCompleted = false;
      this.branchName = "";
      this.pipelineStatus = {
        message: "Starting configuration capture...",
        type: "info",
        progress: 10,
      };

      try {
        const accessToken = await this.getAccessToken();

        // Parse excluded properties and paths
        const excludedProperties = this.parseTextToArray(
          this.excludedPropertiesText
        );
        const excludedPaths = this.parseTextToArray(this.excludedPathsText);

        // Trigger the pipeline
        this.runId = await runCapturePipeline(
          accessToken,
          this.projectId,
          this.pipelineId,
          this.selectedClientCode,
          excludedProperties,
          excludedPaths
        );

        if (this.runId) {
          this.pipelineStatus = {
            message:
              "Capture pipeline started successfully. Monitoring progress...",
            type: "info",
            progress: 20,
          };

          // Start polling for status
          this.startPolling();
        } else {
          throw new Error("Failed to start pipeline");
        }
      } catch (error) {
        console.error("Error triggering capture:", error);
        this.isRunning = false;
        this.pipelineStatus = {
          message: "Failed to trigger capture: " + error.message,
          type: "error",
        };
      }
    },

    startPolling() {
      this.pollInterval = setInterval(async () => {
        try {
          const accessToken = await this.getAccessToken();
          const status = await getPipelineStatus(
            accessToken,
            this.projectId,
            this.pipelineId,
            this.runId
          );

          // Update progress based on status
          if (status.state === "inProgress") {
            this.pipelineStatus.progress = Math.min(
              this.pipelineStatus.progress + 10,
              90
            );
            this.pipelineStatus.message = `Capturing configuration... (${status.state})`;
          } else if (status.state === "completed") {
            clearInterval(this.pollInterval);
            this.isRunning = false;
            this.captureCompleted = true;

            if (status.result === "succeeded") {
              // Generate expected branch name
              this.branchName = `client-${this.selectedClientCode}-capture-${this.runId}`;

              this.pipelineStatus = {
                message: "Configuration captured successfully!",
                type: "success",
                progress: 100,
              };
            } else {
              this.pipelineStatus = {
                message: `Capture failed: ${status.result}`,
                type: "error",
              };
            }
          }
        } catch (error) {
          console.error("Error polling status:", error);
          clearInterval(this.pollInterval);
          this.isRunning = false;
          this.pipelineStatus = {
            message: "Error checking pipeline status",
            type: "error",
          };
        }
      }, 5000); // Poll every 5 seconds
    },

    parseTextToArray(text) {
      if (!text || !text.trim()) return [];

      // Split by newlines or commas, trim whitespace, filter empty
      return text
        .split(/[\n,]/)
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    },

    goToReview() {
      this.$router.push({
        name: "ClientCaptureManagement",
        params: { clientCode: this.selectedClientCode },
      });
    },

    getStatusIcon(type) {
      const icons = {
        info: "⚙️",
        success: "✅",
        error: "❌",
        warning: "⚠️",
      };
      return icons[type] || "ℹ️";
    },

    getStatusTitle(type) {
      const titles = {
        info: "In Progress",
        success: "Success",
        error: "Error",
        warning: "Warning",
      };
      return titles[type] || "Status";
    },

    getAccessToken,
  },
};
</script>

<style scoped>
.trigger-capture-page {
  padding: 24px;
  max-width: 900px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 32px;
}

.page-header h1 {
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: 600;
  color: #1a1a1a;
}

.subtitle {
  margin: 0;
  color: #666;
  font-size: 16px;
}

.content-card {
  background: white;
  padding: 32px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.form-section {
  margin-bottom: 32px;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 14px;
  color: #1a1a1a;
}

.required {
  color: #dc3545;
}

.form-select,
.form-input,
.form-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
}

.form-select:focus,
.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.form-select:disabled,
.form-input:disabled,
.form-textarea:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

.help-text {
  margin: 4px 0 0 0;
  font-size: 13px;
  color: #666;
}

.toggle-advanced {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  padding: 10px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  transition: all 0.2s;
}

.toggle-advanced:hover {
  background: #e9ecef;
}

.advanced-options {
  margin-top: 16px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 4px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.action-section {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}

.btn-primary,
.btn-secondary {
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn-primary:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #545b62;
}

.spinner-small {
  width: 14px;
  height: 14px;
  border: 2px solid #ffffff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.status-section {
  margin-bottom: 24px;
}

.status-card {
  padding: 20px;
  border-radius: 6px;
  border-left: 4px solid;
}

.status-card.info {
  background: #e7f3ff;
  border-color: #007bff;
}

.status-card.success {
  background: #d4edda;
  border-color: #28a745;
}

.status-card.error {
  background: #f8d7da;
  border-color: #dc3545;
}

.status-card.warning {
  background: #fff3cd;
  border-color: #ffc107;
}

.status-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.status-icon {
  font-size: 20px;
}

.status-title {
  font-weight: 600;
  font-size: 16px;
}

.status-message {
  margin: 0;
  color: #495057;
}

.progress-section {
  margin-top: 16px;
}

.progress-bar {
  height: 8px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #007bff;
  transition: width 0.3s ease;
}

.progress-text {
  margin: 8px 0 0 0;
  font-size: 13px;
  color: #666;
}

.branch-info {
  margin-top: 16px;
  padding: 16px;
  background: white;
  border-radius: 4px;
}

.branch-info h4 {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #495057;
}

.branch-info code {
  display: block;
  padding: 8px 12px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-family: "Courier New", monospace;
  font-size: 13px;
  color: #495057;
  margin-bottom: 8px;
}

.info-box {
  padding: 20px;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #dee2e6;
}

.info-box h3 {
  margin: 0 0 16px 0;
  font-size: 16px;
  color: #495057;
}

.info-box ol {
  margin: 0;
  padding-left: 24px;
  color: #495057;
}

.info-box li {
  margin-bottom: 12px;
  line-height: 1.6;
}

.info-box li:last-child {
  margin-bottom: 0;
}

.info-box code {
  padding: 2px 6px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 3px;
  font-family: "Courier New", monospace;
  font-size: 12px;
}
</style>
